import { useMemo, useState } from 'react';
import { formatMoney, formatDate } from '../lib/format';
import {
  useTransactionList,
  TransactionListToolbar,
  StatusBadge,
  Pager,
  ListStates,
  SortableTh,
  type StatusOption,
} from './TransactionList';
import {
  CustomerFilter,
  DateRangeFilter,
} from './TransactionFilters';
import { ViewHeader } from './DashboardLayout';
import {
  useConnection,
  stripeDisputeUrl,
  stripeChargeUrl,
  hubspotContactUrl,
} from './links';
import { ExtLink } from './ExtLink';
import type { DisputeListEntry } from './types';

/**
 * Dashboard → Disputes (M19 T2 + T3).
 *
 * Chargeback ledger: one row per Stripe Dispute. The "needs response" statuses
 * (red) are the daily ops queue — they're the default-landing signal. Evidence
 * due date is shown and turns red when past. Filterable by status (incl. an
 * "Action needed" multi-status combined option), the universal Customer
 * typeahead, and a date-range preset. The parent charge id is shown under the
 * dispute id (linked to the Stripe payment page) — consistent with refunds.
 */

/** Combined value for the "Action needed" preset (needs_response OR warning). */
const ACTION_NEEDED = 'needs_response,warning_needs_response';

const STATUS_OPTIONS: StatusOption[] = [
  { value: ACTION_NEEDED, label: 'Action needed', tone: 'danger' },
  { value: 'needs_response', label: 'Needs response', tone: 'danger' },
  { value: 'warning_needs_response', label: 'Warning — needs response', tone: 'danger' },
  { value: 'under_review', label: 'Under review', tone: 'warn' },
  { value: 'warning_under_review', label: 'Warning — under review', tone: 'warn' },
  { value: 'won', label: 'Won', tone: 'success' },
  { value: 'lost', label: 'Lost', tone: 'neutral' },
  { value: 'prevented', label: 'Prevented', tone: 'neutral' },
  { value: 'warning_closed', label: 'Warning — closed', tone: 'neutral' },
];

export function Disputes() {
  // The disputes-specific evidence-due SLA filter (managed locally +
  // passed to the hook via extraParams so it's only applied to disputes).
  const [evidenceDueBefore, setEvidenceDueBefore] = useState<number | undefined>(
    undefined
  );
  const extraParams = useMemo(
    () => (evidenceDueBefore != null ? { evidenceDueBefore } : {}),
    [evidenceDueBefore]
  );
  const list = useTransactionList<DisputeListEntry>(
    'admin/disputes',
    'status',
    25,
    extraParams
  );
  const {
    data,
    loading,
    error,
    refetch,
    status,
    setStatus,
    customer,
    setCustomer,
    dateRange,
    setDateRange,
    sort,
    dir,
    toggleSort,
    setOffset,
  } = list;

  return (
    <>
      <ViewHeader
        title="Disputes"
        subtitle="Stripe Disputes / chargebacks. Action-needed statuses need evidence."
      />
      <TransactionListToolbar
        status={status}
        setStatus={setStatus}
        statusOptions={STATUS_OPTIONS}
        statusLabel="Status"
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <CustomerFilter
          selected={customer}
          selectedLabel={null}
          onSelect={(id) => {
            setCustomer(id);
            setOffset(0);
          }}
          onClear={() => setCustomer(null)}
        />
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
        {evidenceDueBefore != null && (
          <button
            type="button"
            onClick={() => setEvidenceDueBefore(undefined)}
            className="rounded-full bg-warn-500/10 px-2 py-1 text-xs text-warn-600 hover:bg-warn-500/20"
            title="Clear evidence-due filter"
          >
            ⏰ due ≤ {formatDate(evidenceDueBefore)} ✕
          </button>
        )}
      </div>

      {(loading || error || (data && data.items.length === 0)) && (
        <ListStates
          loading={loading}
          error={error}
          empty={!!data && data.items.length === 0}
          onRetry={refetch}
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Dispute</th>
                  <th className="px-4 py-2 font-medium">Customer</th>
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <th className="px-4 py-2 font-medium">Reason</th>
                  <SortableTh
                    label="Evidence due"
                    sortKey="evidenceDue"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <SortableTh
                    label="Created"
                    sortKey="created"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((d) => (
                  <DisputeRow key={d.id} dispute={d} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={data.total}
            offset={data.offset}
            pageSize={data.limit}
            setOffset={setOffset}
          />
        </>
      )}
    </>
  );
}

function DisputeRow({ dispute }: { dispute: DisputeListEntry; }) {
  const { portalId, stripeAccountId } = useConnection();
  const stripeUrl = stripeDisputeUrl(stripeAccountId, dispute.id);
  // Parent charge id under the dispute id — same pattern as refunds.
  const chargeUrl = stripeChargeUrl(stripeAccountId, dispute.stripeChargeId);
  const hsUrl = hubspotContactUrl(portalId, dispute.customerEmail ?? '');
  const nowSec = Math.floor(Date.now() / 1000);
  const overdue =
    dispute.evidenceDueBy != null && dispute.evidenceDueBy < nowSec;
  const dueSoon =
    !overdue &&
    dispute.evidenceDueBy != null &&
    dispute.evidenceDueBy - nowSec < 7 * 24 * 3600;
  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-2">
        {stripeUrl ? (
          <ExtLink href={stripeUrl} label={dispute.id} title="View in Stripe" />
        ) : (
          <span className="text-xs text-ink-600">{dispute.id}</span>
        )}
        {dispute.stripeChargeId && (
          <div className="mt-0.5 font-mono text-xs text-ink-400">
            {chargeUrl ? (
              <ExtLink href={chargeUrl} label={dispute.stripeChargeId} title="View charge in Stripe" />
            ) : (
              dispute.stripeChargeId
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-col">
          <span className="text-ink-800">{dispute.customerName ?? '—'}</span>
          {dispute.customerEmail && (
            <span className="text-xs text-ink-400">
              {hsUrl ? (
                <ExtLink href={hsUrl} label={dispute.customerEmail} title="View in HubSpot" />
              ) : (
                dispute.customerEmail
              )}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-right text-ink-800">
        {formatMoney(dispute.amountMinor, dispute.currency)}
      </td>
      <td className="px-4 py-2">
        <StatusBadge status={dispute.status} options={STATUS_OPTIONS} />
      </td>
      <td className="px-4 py-2 text-xs text-ink-600">
        {dispute.reason ?? '—'}
      </td>
      <td
        className={[
          'px-4 py-2 whitespace-nowrap text-xs',
          overdue
            ? 'font-semibold text-danger-600'
            : dueSoon
              ? 'text-warn-600'
              : 'text-ink-500',
        ].join(' ')}
        title={dispute.evidenceDueBy != null ? formatDate(dispute.evidenceDueBy) : undefined}
      >
        {dispute.evidenceDueBy != null ? formatDate(dispute.evidenceDueBy) : '—'}
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-xs text-ink-500" title={formatDate(dispute.createdAt)}>
        {formatDate(dispute.createdAt)}
      </td>
    </tr>
  );
}
